import type { DomainCheck } from "@onestore/api/domainSearch"
import type { BasketPatchItem } from "@onestore/api/types"
import type { CloudBlueProductType } from "@onestore/onestore-store-common/api/shapes"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import type { SelectedBundle } from "@gatsby-plugin-bonus/types"
import get from "lodash/get"
import type { ProductBoxBundle } from "~/fragments/bundle"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import { getBundleBasketPatchItem } from "~/helpers/getBundleBasketPatchItem"
import { isCloudBluePlanUpsell } from "~/helpers/isCloudBluePlanUpsell"
import isEmpty from "~/lib/isEmpty"
import { addItemsToBasket } from "../basket/actions"
import { BasketActionSource } from "../basket/constants"
import type { AppDispatch, AppState } from "../reducer"
import {
  buildBonusBasketItem,
  getProductBasketWithResources,
} from "./actions-bonus"
import { resetDomainSearch } from "./actions-domain-search-box"
import {
  type BonusChangeProductPeriodFailureAction,
  type BonusChangeProductPeriodRequestAction,
  type BonusChangeProductPeriodSuccessAction,
  BonusProductActionsType,
  fetchDefinitionsByAliases,
} from "./actions-product"
import { BonusActions } from "./constants"
import {
  getAxDrawerSelectedUpsellSelector,
  getBonusAxDrawerBundle,
  getBonusAxDrawerBundleProduct,
  getProduct,
  isBonusAxDrawerBundleSelected,
} from "./selectors"
import { getChosenPeriod } from "./utils"

export type AxDrawerShowAction = {
  type: BonusActions.AX_DRAWER_SHOW
  saleConfiguration: SaleConfiguration
  bundle?: ProductBoxBundle
  isBundleSelected?: boolean
}

export type AxDrawerCloseAction = {
  type: BonusActions.AX_DRAWER_CLOSE
}

export type AxDrawerDefinitionsLoadedAction = {
  type: BonusActions.AX_DRAWER_DEFINITIONS_LOADED
  definitions: CloudBlueProductType[]
  saleConfiguration: SaleConfiguration
}

export type AxDrawerToggleUpsellAction = {
  type: BonusActions.AX_DRAWER_TOGGLE_UPSELL
  alias: string
}

export type AxDrawerToggleBundleAction = {
  type: BonusActions.AX_DRAWER_TOGGLE_BUNDLE
}

export function showAxDrawer(
  saleConfiguration: SaleConfiguration,
  bundle?: ProductBoxBundle,
  isBundleSelected: boolean = false
) {
  return async (dispatch: AppDispatch) => {
    dispatch(resetDomainSearch())

    dispatch({
      type: BonusActions.AX_DRAWER_SHOW,
      saleConfiguration,
      bundle,
      isBundleSelected,
    } as AxDrawerShowAction)

    await dispatch(requestAxDrawerDefinitions(saleConfiguration, bundle))
  }
}

export function requestAxDrawerDefinitions(
  saleConfiguration: SaleConfiguration,
  bundle?: ProductBoxBundle
) {
  return async (dispatch: AppDispatch) => {
    const mainAlias = get(
      saleConfiguration,
      "flatData.defaultCloudBluePlan[0].flatData.alias",
      undefined
    )

    const upsellAliases = (saleConfiguration.flatData.upsell ?? [])
      .filter(isCloudBluePlanUpsell)
      .map((upsellItem) => upsellItem.flatData.alias)

    // The bundle's own plan (the product-box's "add alongside" bundle,
    // e.g. an SSL certificate checkbox) — NOT saleConfiguration.bundlePlan,
    // which is a separate, "swap main plan" only field (see
    // ProductBoxWithSaleConfiguration's isBundleReplacingMainPlan). Without
    // this, bundleProduct never resolves for the "add alongside" case, so
    // its price never appears and the drawer's total never accounts for it.
    const bundleAlias = get(
      bundle,
      "flatData.plan[0].flatData.alias",
      undefined
    )

    try {
      const definitions = await fetchDefinitionsByAliases([
        mainAlias,
        ...upsellAliases,
        bundleAlias,
      ])

      dispatch({
        type: BonusActions.AX_DRAWER_DEFINITIONS_LOADED,
        definitions,
        saleConfiguration,
      } as AxDrawerDefinitionsLoadedAction)
    } catch {
      // do nothing, the drawer will fallback to showing only the main product without upsell or bundle details
    }
  }
}

/**
 * Reprices the ax-drawer's main product for a candidate license/resource
 * quantity before it exists in the real basket (BASKET_CHANGE_QUANTITY can
 * only patch an already-added item — see requestChangeProductPeriod). Only
 * updates `state.bonus.product.totalPrices`; the quantity itself is applied
 * to the basket item once the drawer adds it, via requestAddAxDrawerItemsToBasket.
 */
export function requestChangeAxDrawerProductQuantity(quantity: number) {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    const product = getProduct(getState())
    const period = product ? getChosenPeriod(product) : undefined

    if (!product || !period) {
      return
    }

    dispatch({
      type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_REQUEST,
      status: HTTP_STATUS.CONTINUE,
      period,
    } as BonusChangeProductPeriodRequestAction)

    try {
      const totalPrices = await getProductBasketWithResources(
        product,
        period.id,
        [],
        null,
        quantity
      )

      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_SUCCESS,
        status: HTTP_STATUS.OK,
        period,
        ...totalPrices,
      } as BonusChangeProductPeriodSuccessAction)
    } catch (error) {
      dispatch({
        type: BonusProductActionsType.BONUS_CHANGE_PRODUCT_PERIOD_FAILURE,
        status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
        error: error instanceof Error ? error.message : undefined,
      } as BonusChangeProductPeriodFailureAction)
    }
  }
}

export function closeAxDrawer() {
  return (dispatch: AppDispatch) => {
    dispatch(resetDomainSearch())

    dispatch({
      type: BonusActions.AX_DRAWER_CLOSE,
    } as AxDrawerCloseAction)
  }
}

export const toggleAxDrawerUpsell = (
  alias: string
): AxDrawerToggleUpsellAction => ({
  type: BonusActions.AX_DRAWER_TOGGLE_UPSELL,
  alias,
})

export const toggleAxDrawerBundle = (): AxDrawerToggleBundleAction => ({
  type: BonusActions.AX_DRAWER_TOGGLE_BUNDLE,
})

/**
 * Builds a single basket item for a domain bundle (the main domain together
 * with its nested child domains). Mirrors the shape produced by
 * addDomainsToBasket so the bundle can go into the same single request.
 */
function buildBundleItem(bundle: SelectedBundle): BasketPatchItem {
  const bundleChildren = isEmpty(bundle.childDomains)
    ? undefined
    : bundle.childDomains.map((child) => ({
        plan: child.plan,
        quantity: 1,
        parameters: { domain: child.fqdn },
        ignoreHack: false,
      }))

  return {
    plan: bundle.mainDomain.plan,
    quantity: 1,
    parameters: { domain: bundle.mainDomain.fqdn },
    ignoreHack: false,
    children: bundleChildren,
  }
}

/**
 * Adds all ax-drawer items (domain bundles, upsells, domains and the main
 * product) to the basket in a SINGLE `basketPatch` request instead of separate
 * per-item calls. Returns the added items (lastItems), or undefined when there
 * is nothing to add.
 */
export function requestAddAxDrawerItemsToBasket(
  selectedDomains: DomainCheck.Result[] = [],
  selectedDomainBundles: SelectedBundle[] = [],
  promoCode: string | null = null,
  quantity?: number
) {
  return async (dispatch: AppDispatch, getState: { (): AppState }) => {
    const state = getState()
    const product = getProduct(state)
    const selectedUpsell = getAxDrawerSelectedUpsellSelector(state)
    const bundle = isBonusAxDrawerBundleSelected(state)
      ? getBonusAxDrawerBundle(state)
      : undefined

    const items: BasketPatchItem[] = []

    for (const bundle of selectedDomainBundles) {
      items.push(buildBundleItem(bundle))
    }

    for (const upsell of selectedUpsell) {
      items.push(buildBonusBasketItem(upsell, null, getState))
    }

    for (const domain of selectedDomains) {
      if (domain.plan_id !== null) {
        items.push(
          buildBonusBasketItem(
            { planId: domain.plan_id },
            null,
            getState,
            { domain: domain.fqdn },
            true
          )
        )
      }
    }

    if (product) {
      const productItem = buildBonusBasketItem(
        product,
        null,
        getState,
        {},
        undefined,
        quantity
      )

      // A swap-configured box carries no bundle at all (see
      // getAxDrawerOpenArgs), and `bundle` above is already undefined when the
      // drawer's own checkbox deselected it (AX_DRAWER_TOGGLE_BUNDLE). Nest
      // whatever is left as a child of the main item so it goes into the
      // basket in the same single request, the same shape the bonus page and
      // ConfigDrawer use for "plan + bundle".
      const bundleItem = getBundleBasketPatchItem(
        bundle,
        getBonusAxDrawerBundleProduct(state)?.chosenPeriodId
      )

      if (bundleItem) {
        productItem.children = [bundleItem]
      }

      items.push(productItem)
    }

    if (items.length === 0) {
      return undefined
    }

    return dispatch(
      addItemsToBasket(
        items,
        null,
        BasketActionSource.BONUS,
        () => {},
        promoCode
      )
    )
  }
}
